import i18n from '../../i18n';
import { SupportedLanguage } from './PDFExporter';

const t = (key: string, opts?: { lng?: string }) => i18n.t(key, opts);

function joinPrimarySecondary(primary: string, secondary?: string): string {
  const p = (primary || '').trim();
  const s = (secondary || '').trim();
  if (!p) return s;
  if (!s || s === p) return p;
  return `${p}${s}`;
}

/** 有次语言且含英文时，英文侧用单字母 I/E 参与拼接（非 INT/EXT） */
function isBilingualWithEnglish(
  lng: SupportedLanguage,
  secondaryLng?: SupportedLanguage
): boolean {
  return !!secondaryLng && (lng === 'en' || secondaryLng === 'en');
}

function interiorAbbrevForExport(
  lang: SupportedLanguage,
  bilingualWithEnglish: boolean
): string {
  if (bilingualWithEnglish && lang === 'en') return 'I';
  return t('export.interiorShort', { lng: lang });
}

function exteriorAbbrevForExport(
  lang: SupportedLanguage,
  bilingualWithEnglish: boolean
): string {
  if (bilingualWithEnglish && lang === 'en') return 'E';
  return t('export.exteriorShort', { lng: lang });
}

/** 导出 PDF/Excel 时内外景缩写，全部来自 export.* i18n */
export function formatInteriorExteriorForExport(
  lng: SupportedLanguage,
  value: string | undefined,
  secondaryLng?: SupportedLanguage
): string {
  if (!value) return '';
  const bilingualEn = isBilingualWithEnglish(lng, secondaryLng);

  if (value === 'INTERIOR' || value === 'EXTERIOR') {
    const primary =
      value === 'INTERIOR'
        ? interiorAbbrevForExport(lng, bilingualEn)
        : exteriorAbbrevForExport(lng, bilingualEn);
    const secondary = secondaryLng
      ? value === 'INTERIOR'
        ? interiorAbbrevForExport(secondaryLng, bilingualEn)
        : exteriorAbbrevForExport(secondaryLng, bilingualEn)
      : undefined;
    return joinPrimarySecondary(primary, secondary);
  }

  if (value === 'INTERIOR_EXTERIOR') {
    if (bilingualEn) return 'I/E';
    return t('export.interiorExteriorShort', { lng });
  }
  return value;
}

/** 导出 PDF/Excel 时日夜缩写，全部来自 export.* i18n */
export function formatDayNightForExport(
  lng: SupportedLanguage,
  value: string | undefined,
  secondaryLng?: SupportedLanguage
): string {
  if (!value) return '';
  const mapPrimary: Record<string, string> = {
    MORNING: t('export.morningShort', { lng }),
    DAY: t('export.dayShort', { lng }),
    DUSK: t('export.duskShort', { lng }),
    NIGHT: t('export.nightShort', { lng }),
  };
  const primary = mapPrimary[value] || value;
  if (!secondaryLng) return primary;
  const mapSecondary: Record<string, string> = {
    MORNING: t('export.morningShort', { lng: secondaryLng }),
    DAY: t('export.dayShort', { lng: secondaryLng }),
    DUSK: t('export.duskShort', { lng: secondaryLng }),
    NIGHT: t('export.nightShort', { lng: secondaryLng }),
  };
  return joinPrimarySecondary(primary, mapSecondary[value]);
}
